/**
 * Wizard Step 2 — Household.
 *
 * Consolidates what used to be three separate intro screens (spouse,
 * kids, pets) into a single multi-select. Each check toggles the
 * corresponding HouseholdAnswers flag; no selections ≠ locked — the
 * user can continue with all false, which just means they declined
 * all three.
 */
import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { WizardShell } from '../components/WizardShell';
import { WizardCheckOption } from '../components/WizardCheckOption';
import { useWizard } from '../WizardContext';
import { analytics } from '../../../services/analytics/analytics';
import { AnalyticsEvent } from '../../../services/analytics/events';

const WIZARD_STEP = 2;
const TOTAL_STEPS = 6;

export const WizardHouseholdScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const { answers, setHousehold } = useWizard();
  const [hasSpouse, setHasSpouse] = useState(answers.household.hasSpouse);
  const [hasChildren, setHasChildren] = useState(answers.household.hasChildren);
  const [hasPets, setHasPets] = useState(answers.household.hasPets);

  useEffect(() => {
    analytics.track(AnalyticsEvent.WizardStepShown, { step: 'household', number: WIZARD_STEP });
  }, []);

  const handleContinue = () => {
    setHousehold({ hasSpouse, hasChildren, hasPets });
    analytics.track(AnalyticsEvent.WizardStepCompleted, {
      step: 'household',
      has_spouse: hasSpouse,
      has_children: hasChildren,
      has_pets: hasPets,
    });
    navigation.navigate('WizardRolesIntro');
  };

  return (
    <WizardShell
      stepNumber={WIZARD_STEP}
      totalSteps={TOTAL_STEPS}
      title="Who's in your household?"
      subtitle="Check everyone that applies. We'll add the right information to your priority list. If none apply, just tap Continue."
      primaryLabel="Continue"
      onPrimaryPress={handleContinue}
      onBackPress={() => navigation.goBack()}
    >
      <View>
        <WizardCheckOption
          label="Spouse or partner"
          description="We'll help you organize shared accounts and wishes"
          selected={hasSpouse}
          onToggle={() => setHasSpouse(s => !s)}
        />
        <WizardCheckOption
          label="Children"
          description="Guardianship, school info, medical records"
          selected={hasChildren}
          onToggle={() => setHasChildren(c => !c)}
        />
        <WizardCheckOption
          label="Pets"
          description="Vet info, medications, future care"
          selected={hasPets}
          onToggle={() => setHasPets(p => !p)}
        />
      </View>
    </WizardShell>
  );
};
